import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CaptureHealth } from '@contracts/common';
import type { MeetingDetail } from '@contracts/meetings';
import type { NoteTemplate, Writeup } from '@contracts/templates';
import {
  useSlack,
  useSlackChannels,
  useTemplates,
  useWriteupActions,
  useWriteups,
} from '../api/queries';
import { Button, Card, Confirm, Empty, MenuButton, Modal, SkeletonRows, useNotices } from '../ui';
import type { MenuItem } from '../ui';
import { since } from '../lib/time';
import { copyText } from '../lib/note';
import { useUrlParam } from '../lib/url-tab';
import { EditableNote } from '../editor/EditableNote';
import { NoteText } from '../editor/NoteText';
import { SendNoteDm } from './SendNoteDm';

/** The meeting is not over: there is nothing to write up yet. The meeting page's own list. */
const HAPPENING: CaptureHealth[] = ['SCHEDULED', 'JOINING', 'WAITING_ROOM', 'RECORDING', 'PAUSED'];

const KIND_LABEL: Record<NoteTemplate['kind'], string> = {
  stock: 'Built in',
  workspace: 'Shared in the workspace',
  personal: 'Only you',
};

/**
 * The Full note tab: the note, and every write-up of it (PRD 61).
 *
 * Vladimir, 2026-09-25: *"Base full note shall not be replaced, think of what
 * is the best way to do this and how to show it to user"*. So the full note is
 * always the first version and the one the tab opens on; each write-up is a
 * version of its own beside it, named for its template and saying so in its
 * own first line — never a replacement nobody asked for, and never a second
 * "note" that could be mistaken for the record.
 */
export function NoteVersions({
  meeting,
  full,
  lines,
}: {
  meeting: MeetingDetail;
  /** The full note, exactly as the tab showed it before write-ups existed. */
  full: ReactNode;
  /** Lines in the transcript, null while it loads. */
  lines: number | null;
}): ReactElement {
  // ⚠ The transcript's reader: a write-up is written from every word said,
  // and a link reader is given the note, never the words.
  const allowed = meeting.access.canReadTranscript;
  const writeups = useWriteups(meeting.id, allowed);
  const all = writeups.data ?? [];
  // The series' own note (PRD 65): its chip first, and what the tab opens on.
  // Vladimir, 2026-10-08: "it shall open on that note, not the main".
  const main = all.find((w) => w.id === meeting.seriesNote?.writeupId) ?? null;
  const list = main ? [main, ...all.filter((w) => w !== main)] : all;
  // Which version is open, in the address (`?version=<write-up>`) so a reload
  // and Back come back to it. None is the series' note where there is one,
  // else the full note — which is then `?version=full`, said out loud.
  const [version, setVersion] = useUrlParam('version');
  const opensOn = main?.id ?? 'full';
  const picked = version ?? opensOn;
  const setPicked = useCallback(
    (id: string) => setVersion(id === opensOn ? null : id),
    [setVersion, opensOn],
  );
  const shown = picked === 'full' ? null : (list.find((w) => w.id === picked) ?? null);
  // A write-up deleted meanwhile, here or elsewhere, falls back to the note.
  useEffect(() => {
    if (picked !== 'full' && writeups.data && !writeups.data.some((w) => w.id === picked)) {
      setPicked('full');
    }
  }, [picked, writeups.data, setPicked]);
  if (!allowed) return <>{full}</>;

  const versionChip = (w: Writeup) => (
    <button
      key={w.id}
      type="button"
      role="tab"
      aria-selected={picked === w.id}
      className={`chip ${picked === w.id ? 'on' : ''}`}
      onClick={() => setPicked(w.id)}
    >
      {w.templateName}
      {w.status === 'writing' ? (
        <span className="version-dot writing" aria-label="being written" />
      ) : w.status === 'failed' ? (
        <span className="version-dot failed" aria-label="could not be written" />
      ) : null}
    </button>
  );

  return (
    <div className="stack">
      <div className="cluster versions" role="tablist" aria-label="Versions of this note">
        {main ? versionChip(main) : null}
        <button
          type="button"
          role="tab"
          aria-selected={picked === 'full'}
          className={`chip ${picked === 'full' ? 'on' : ''}`}
          onClick={() => setPicked('full')}
        >
          Full note
        </button>
        {list.filter((w) => w !== main).map(versionChip)}
        <UseTemplate meeting={meeting} lines={lines} writeups={list} onAsked={setPicked} />
      </div>
      {shown ? <WriteupView meeting={meeting} w={shown} /> : full}
    </div>
  );
}

/** "✦ Use a template": every template this person may use, and the way to make another. */
function UseTemplate({
  meeting,
  lines,
  writeups,
  onAsked,
}: {
  meeting: MeetingDetail;
  lines: number | null;
  /** The write-ups there are: one somebody changed by hand is not replaced without a word. */
  writeups: Writeup[];
  onAsked: (writeupId: string) => void;
}): ReactElement {
  const templates = useTemplates();
  const { apply } = useWriteupActions(meeting.id);
  const navigate = useNavigate();
  const { show } = useNotices();
  const [replacing, setReplacing] = useState<{ t: NoteTemplate; w: Writeup } | null>(null);
  const why = HAPPENING.includes(meeting.captureHealth)
    ? 'Write it up when the meeting is over'
    : lines === 0
      ? 'Nothing was said in this meeting'
      : undefined;
  const use = (t: NoteTemplate) =>
    apply.mutate(t.id, {
      onSuccess: (w) => {
        onAsked(w.id);
        show({
          tone: 'success',
          text: `Writing it up as “${t.name}” — the full note stays as it is.`,
        });
      },
      onError: (e) => show({ tone: 'failure', text: e.message }),
    });
  const items: MenuItem[] = (templates.data ?? []).map((t) => ({
    id: t.id,
    label: t.name,
    sub: `${KIND_LABEL[t.kind]}${t.description ? ` · ${t.description}` : ''}`,
    disabledBecause: why,
    onSelect: () => {
      const edited = writeups.find((w) => w.templateId === t.id && w.editedAt);
      if (edited) setReplacing({ t, w: edited });
      else use(t);
    },
  }));
  return (
    <>
      <MenuButton
        label={apply.isPending ? 'Asking…' : '✦ Use a template'}
        size="sm"
        variant="quiet"
        align="left"
        items={items}
        fixed={[{ label: 'Manage templates…', onSelect: () => navigate('/templates') }]}
        search
      />
      {replacing ? (
        <ReplaceEdits
          w={replacing.w}
          working={apply.isPending}
          onClose={() => setReplacing(null)}
          onConfirm={() => {
            use(replacing.t);
            setReplacing(null);
          }}
        />
      ) : null}
    </>
  );
}

/** Writing again over somebody's edits: said, and asked, before it happens. */
function ReplaceEdits({
  w,
  working,
  onClose,
  onConfirm,
}: {
  w: Writeup;
  working: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactElement {
  return (
    <Confirm
      title="Write it again?"
      noun="Write it again"
      working={working}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      {w.editedBy?.name ?? 'Somebody'} changed “{w.templateName}” by hand
      {w.editedAt ? ` ${since(w.editedAt)}` : ''}. Writing it again replaces those changes with a
      fresh write-up of the meeting. The full note is not touched.
    </Confirm>
  );
}

/** One write-up: what it is, what it says, and what can be done with it. */
function WriteupView({ meeting, w }: { meeting: MeetingDetail; w: Writeup }): ReactElement {
  const { rewrite, remove, edit } = useWriteupActions(meeting.id);
  const [sending, setSending] = useState(false);
  const [dming, setDming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const { show } = useNotices();
  const by = w.askedBy ? `by ${w.askedBy.name ?? 'somebody'}` : 'by the series, after the meeting';
  const edited =
    w.editedAt && w.status === 'ready'
      ? ` · edited ${since(w.editedAt)}${w.editedBy?.name ? ` by ${w.editedBy.name}` : ''}`
      : '';
  const again = () =>
    rewrite.mutate(w.id, {
      onSuccess: () => show({ tone: 'success', text: 'Writing it again.' }),
      onError: (e) => show({ tone: 'failure', text: e.message }),
    });
  // Somebody's edits are not written over without a word.
  const askAgain = () => (w.editedAt ? setReplacing(true) : again());

  return (
    <Card
      title={w.templateName}
      aside={
        <span className="mono">
          {w.status === 'ready' && w.writtenAt
            ? `written ${since(w.writtenAt)} · ${by}${edited}`
            : w.status === 'writing'
              ? 'being written…'
              : 'could not be written'}
        </span>
      }
    >
      <div className="stack">
        {/* Said on the version itself, because it is the question a reader
            has: is this the record, or one reading of it? */}
        <p className="t-secondary writeup-note">
          Written with a template, beside the full note — the full note is unchanged.
        </p>
        {w.status === 'writing' ? (
          <SkeletonRows rows={5} />
        ) : w.status === 'failed' ? (
          <Empty
            tone="bad"
            lead={w.error ?? 'It could not be written.'}
            action={
              <Button size="sm" onClick={again}>
                Try again
              </Button>
            }
          />
        ) : w.canEdit ? (
          // Changed where it is read, like the full note — kept as it is typed.
          <EditableNote
            key={w.id}
            value={w.body}
            onSave={(body) => edit.mutateAsync({ writeupId: w.id, body })}
            label="The write-up"
            placeholder="Write it up…"
          />
        ) : (
          <div className="note" style={{ maxWidth: '68ch' }}>
            <NoteText>{w.body ?? ''}</NoteText>
          </div>
        )}
        {w.posts.length ? (
          <p className="mono">
            sent to {w.posts.map((p) => `#${p.channelName}`).join(', ')} · last{' '}
            {since(w.posts[w.posts.length - 1].at)}
          </p>
        ) : null}
        <div className="cluster">
          <Button
            size="sm"
            disabled={w.status !== 'ready'}
            onClick={() =>
              void copyText(w.body ?? '').then(
                () => show({ tone: 'success', text: 'Copied — Markdown, ready to paste.' }),
                () => show({ tone: 'failure', text: 'The browser did not let it be copied.' }),
              )
            }
          >
            Copy
          </Button>
          <Button size="sm" disabled={w.status !== 'ready'} onClick={() => setSending(true)}>
            Send to Slack
          </Button>
          <Button size="sm" disabled={w.status !== 'ready'} onClick={() => setDming(true)}>
            Send to a person
          </Button>
          <Button
            size="sm"
            variant="quiet"
            disabled={w.status === 'writing'}
            working={rewrite.isPending ? 'Asking…' : false}
            onClick={askAgain}
          >
            Rewrite
          </Button>
          {w.canDelete ? (
            <Button size="sm" variant="quiet" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      {sending ? (
        <SendToSlack meetingId={meeting.id} w={w} onClose={() => setSending(false)} />
      ) : null}
      {dming ? (
        <SendNoteDm
          meetingId={meeting.id}
          attendees={meeting.attendees}
          writeup={{ id: w.id, templateName: w.templateName }}
          onClose={() => setDming(false)}
        />
      ) : null}
      {replacing ? (
        <ReplaceEdits
          w={w}
          working={rewrite.isPending}
          onClose={() => setReplacing(false)}
          onConfirm={() => {
            again();
            setReplacing(false);
          }}
        />
      ) : null}
      {deleting ? (
        <Confirm
          title="Delete this write-up?"
          noun="Delete the write-up"
          working={remove.isPending}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            remove.mutate(w.id, {
              onSuccess: () => {
                setDeleting(false);
                show({ tone: 'success', text: 'Deleted — the full note is as it was.' });
              },
              onError: (e) => show({ tone: 'failure', text: e.message }),
            })
          }
        >
          The full note is not touched, and the meeting can be written up with this template again
          at any time.
        </Confirm>
      ) : null}
    </Card>
  );
}

/**
 * To a channel, as a message everyone there can read — which is the point:
 * Fellow shows such a summary only to people with a paid seat (Aleksandr, at
 * the AI Talks).
 */
function SendToSlack({
  meetingId,
  w,
  onClose,
}: {
  meetingId: string;
  w: Writeup;
  onClose: () => void;
}): ReactElement {
  const slack = useSlack();
  const connected = slack.data?.connected === true;
  const channels = useSlackChannels(connected);
  const { slack: send } = useWriteupActions(meetingId);
  const [channel, setChannel] = useState<{ id: string; name: string } | null>(null);
  const { show } = useNotices();
  return (
    <Modal
      title="Send to Slack"
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!channel}
            working={send.isPending ? 'Sending…' : false}
            onClick={() => {
              if (!channel) return;
              send.mutate(
                { writeupId: w.id, body: { channelId: channel.id, channelName: channel.name } },
                {
                  onSuccess: () => {
                    show({ tone: 'success', text: `Sent to #${channel.name}.` });
                    onClose();
                  },
                  onError: (e) => show({ tone: 'failure', text: e.message }),
                },
              );
            }}
          >
            Send
          </Button>
        </>
      }
    >
      {!connected ? (
        <Empty lead="Slack is not connected to this workspace.">
          An administrator connects it once, under Settings → Connected apps.
        </Empty>
      ) : (
        <div className="stack">
          <p className="t-secondary">
            “{w.templateName}” goes as a message everyone in the channel can read — people without a
            Ptitim account included — with a link back to the meeting.
          </p>
          <MenuButton
            label={channel ? `# ${channel.name}` : 'Choose a channel'}
            align="left"
            search
            items={(channels.data ?? []).map((c) => ({
              id: c.id,
              label: `${c.isPrivate ? '🔒' : '#'} ${c.name}`,
              current: c.id === channel?.id,
              onSelect: () => setChannel({ id: c.id, name: c.name }),
            }))}
          />
          <span className="why">
            a private channel shows once Ptitim is in it — type /invite @Ptitim there
          </span>
        </div>
      )}
    </Modal>
  );
}
